/**
 * Left column of the Messages page: inbox summary, "New conversation", and one
 * row per thread in the order the server returns them (newest first).
 */

import { Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ContactInitials } from "@/features/realtor/components/contact-initials";
import type { Thread } from "@/features/realtor/services/realtor-crm-api.service";
import { formatRelativeTime } from "@/features/realtor/utils/relative-time";
import { Pill, StudioButton } from "@/features/studio/theme";

interface ThreadListProps {
	threads: Thread[] | undefined;
	isPending: boolean;
	error: Error | null;
	onRetry: () => void;
	selectedId: string | null;
	onSelect: (contactId: string) => void;
	onNewConversation: () => void;
}

export function ThreadList({
	threads,
	isPending,
	error,
	onRetry,
	selectedId,
	onSelect,
	onNewConversation,
}: ThreadListProps) {
	const { t } = useTranslation();
	const awaiting = threads?.filter((thread) => thread.awaitingReply).length;

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="flex items-center justify-between gap-2 border-b border-dashboard-border px-4 py-3">
				<p
					className="min-w-0 text-xs text-dashboard-muted"
					data-testid="messages-inbox-summary"
				>
					<span className="font-semibold text-dashboard-text">
						{t("realtor.messages.inbox")}
					</span>
					{threads && (
						<>
							{" · "}
							{t("realtor.messages.threadCount", { count: threads.length })}
							{" · "}
							{t("realtor.messages.awaitingCount", { count: awaiting })}
						</>
					)}
				</p>
				<StudioButton
					size="xs"
					className="shrink-0"
					icon={Plus}
					onClick={onNewConversation}
				>
					{t("realtor.messages.newConversation")}
				</StudioButton>
			</div>

			{isPending && (
				<div className="space-y-3 p-4" data-testid="messages-threads-loading">
					<Skeleton className="h-14 w-full bg-dashboard-border" />
					<Skeleton className="h-14 w-full bg-dashboard-border" />
					<Skeleton className="h-14 w-full bg-dashboard-border" />
				</div>
			)}

			{error && (
				<div className="space-y-3 p-4" role="alert">
					<p className="text-sm text-dashboard-muted">
						{error.message || t("realtor.messages.loadFailed")}
					</p>
					<StudioButton
						variant="secondary"
						size="xs"
						icon={RefreshCw}
						onClick={onRetry}
					>
						{t("realtor.messages.retry")}
					</StudioButton>
				</div>
			)}

			{threads && threads.length > 0 && (
				<ul
					className="min-h-0 flex-1 overflow-y-auto"
					aria-label={t("realtor.messages.inbox")}
				>
					{threads.map((thread) => (
						<ThreadRow
							key={thread.contact.id}
							thread={thread}
							selected={thread.contact.id === selectedId}
							onSelect={onSelect}
						/>
					))}
				</ul>
			)}
		</div>
	);
}

function ThreadRow({
	thread,
	selected,
	onSelect,
}: {
	thread: Thread;
	selected: boolean;
	onSelect: (contactId: string) => void;
}) {
	const { t, i18n } = useTranslation();
	const { contact, lastMessage } = thread;
	const snippet =
		lastMessage.direction === "outbound"
			? t("realtor.messages.snippetYou", { body: lastMessage.body })
			: lastMessage.body;

	return (
		<li data-testid="messages-thread-row">
			<button
				type="button"
				onClick={() => onSelect(contact.id)}
				aria-current={selected ? "true" : undefined}
				className={`flex w-full items-start gap-3 border-b border-dashboard-border px-4 py-3 text-left transition-colors hover:bg-dashboard-surface ${
					selected ? "bg-dashboard-surface" : ""
				}`}
			>
				<Avatar className="h-9 w-9 shrink-0 border border-dashboard-border">
					<AvatarFallback className="bg-dashboard-border text-[11px] font-semibold text-dashboard-text">
						<ContactInitials name={contact.name} />
					</AvatarFallback>
				</Avatar>
				<span className="min-w-0 flex-1">
					<span className="flex items-baseline justify-between gap-2">
						<span className="truncate text-sm font-medium text-dashboard-text">
							{contact.name}
						</span>
						<span className="shrink-0 text-[11px] text-dashboard-muted">
							{formatRelativeTime(
								lastMessage.occurredAt,
								i18n.language,
								t("realtor.time.justNow"),
							)}
						</span>
					</span>
					<span className="block text-[11px] text-dashboard-muted">
						{t(`realtor.messages.contactTypes.${contact.type}`)}
					</span>
					<span className="mt-1 block truncate text-xs text-dashboard-muted">
						{snippet}
					</span>
					{thread.awaitingReply && (
						<Pill tone="awaiting" dot className="mt-1.5">
							{t("realtor.messages.awaitingReply")}
						</Pill>
					)}
				</span>
			</button>
		</li>
	);
}
